<template>
	<div class="pd">
		<div class="title-header">Новая запись</div>
		<div class="title-text">
			Пользователь {{ userStore.oms }}
		</div>
	</div>
	<Form>
		<CellGroup inset>
			<Field
				v-model="patient"
				is-link
				readonly
				label="Пациент"
				placeholder="Выберите пациента"
				label-align="top"
				@click="showPatientPicker = true"
				:rules="[{ required: true, message: 'Укажите пациента' }]"
			/>
			<Popup :show="showPatientPicker" position="bottom">
				<Picker
					cancel-button-text="Отмена"
					confirm-button-text="Готово"
					:columns="patients"
					:columns-field-names="{text: 'name', value: 'id', children: 'children'}"
					@confirm="onPatientConfirm"
					@cancel="showPatientPicker = false"
				/>
			</Popup>

			<Field
				v-model="speciality"
				name="speciality"
				is-link
				readonly
				label="Специальность"
				placeholder="Выберите специальность врача"
				label-align="top"
				@click="onSpecialityFieldClick"
				:rules="[{ required: true, message: 'Укажите специальность врача' }]"
			/>
			<Popup :show="showSpecialityPicker" position="bottom">
				<Picker
					cancel-button-text="Отмена"
					confirm-button-text="Готово"
					:columns="specialitiesDisplay"
					:columns-field-names="{text: 'title', value: 'uid', children: 'children'}"
					@confirm="onSpecialityConfirm"
					@cancel="showSpecialityPicker = false"
				/>
			</Popup>

			<Field
				v-model="doctors"
				name="doctors"
				is-link
				readonly
				label="Укажите врачей, если требуется"
				placeholder="Выберите врача"
				label-align="top"
				@click="onDoctorsFieldClick"
				:rules="[{ required: false }]"
			/>
			<Popup :show="showDoctorsPicker" position="bottom">
				<MultiSelect :items="doctorsList" :onClick="onDoctorsConfirm" />
			</Popup>
		</CellGroup>

		<CellGroup inset class="group-padding">
			<Field
				label="Выберите даты, в которые система будет искать свободные места"
				label-align="top">
				<template #input>
					<Calendar
						type="multiple"
						:color="GREEN_COLOR"
						:poppable="false"
						:show-title="false"
						:show-confirm="false"
						:show-mark="false"
						:max-date="calendarMaxDate"
						:default-date="calendarDefaultDates"
						first-day-of-week="1"
						max-range="21"
						range-prompt="Можно выбрать диапазон не более 21 дней"
						@select="onDaysSelect"
						@unselect="onDaysUnselect"
						class="calendar"
					/>
				</template>
			</Field>

			<Field
				label="Можно записываться день&nbsp;в&nbsp;день"
				class="wide-field"
			>
				<template #input>
					<Switch v-model="quickMode" />
				</template>
			</Field>
		</CellGroup>

		<CellGroup inset class="group-padding">
			<div class="form-text">
				Вы можете выбрать периоды времени, в которых искать места.
				Ограничение будет действовать на все выбранные дни.
			</div>

			<SelectableRange
				v-model="timePeriods"
				:start-value="TIME_PERIODS_START"
				:end-value="TIME_PERIODS_END"
			/>

			<div class="form-text" v-if="selectedTimeRangeText" v-html="selectedTimeRangeText" />
		</CellGroup>

		<CellGroup inset class="group-padding" v-if="selectedDoctorScheduleIntersections.length > 0">
			<div class="form-text">
				Проверьте, что выбранные даты и время пересекаются с расписанием врачей, к которым вы хотите попасть.
				Если пересечений нет, измените ваш выбор.
			</div>
			<div class="form-text">
				<table>
					<tr v-for="intersection in selectedDoctorScheduleIntersections" :key="intersection.uid">
						<td class="intersection-doctor-name">{{ intersection.doctor }}</td>
						<td class="intersection-status">
							<div v-if="intersection.exists">✅ есть пересечения</div>
							<div v-else>❌ нет пересечений</div>
						</td>
					</tr>
				</table>
			</div>
		</CellGroup>

		<CellGroup inset class="group-padding" v-if="autoGroupDisabled">
			<div class="form-text">
				<Icon name="warn-o" title="Отключена автозапись" color="#bb0000" />
				В рамках выбранной специальности необходимо выбрать конкретных врачей вручную
			</div>
		</CellGroup>

		<CellGroup inset class="group-padding" v-if="isDataLoaded">
			<div class="form-text" v-if="verificationCode">
				Чтобы наш телеграм-бот мог прислать вам уведомление о записи, вам надо привязать свой телеграм к этой заявке.
				Для этого пройдите по
				<a :href="verificationLink" target="_blank" class="telegram-confirmation-link">ссылке подтверждения</a>
				для запуска Телеграма и в приложении нажмите кнопку <strong>START</strong>
			</div>
			<div class="form-text" v-else>
				Телеграм для уведомлений <strong>{{ telegramUsername }}</strong> привязан.
				Вы получите уведомление об успешной записи к врачу.
			</div>
			<!-- <Field
				v-model="contactData"
				name="contactData"
				label="Оставьте почту или телеграм. Мы вас уведомим об успешной записи или если записаться не получится"
				label-align="top"
				placeholder="email или @телеграм"
				:rules="[{ required: true, message: 'Оставьте контакт для уведомлений' }]"
			/> -->
		</CellGroup>
		<div class="pd">
			<Button round block type="primary" loading loading-text="Бронируем" disabled v-if="isCreatingAppointment" />
			<Button round block type="primary" @click="onSubmit" v-else>
				Начать бронирование
			</Button>
		</div>
	</Form>
</template>

<script>
import { ref } from 'vue';
import {
	Form, Field, CellGroup, Button, Popup, Picker, Switch,
	Calendar, TimePicker, Icon, showToast, showNotify,
} from 'vant';

import MultiSelect from '@/components/common/MultiSelect';
import SelectableRange from '@/components/common/SelectableRange';
import { pluralize, arrayRange, GREEN_COLOR } from '@/components/common/utils';
import {
	getDayStart,
	datesToStrings,
	getSelectedTimeRangeText,
	getAllSchedulesIntersectionsFormatSafe,
	TIME_PERIODS_START,
	TIME_PERIODS_END,
} from '@/components/common/dateUtils';
import { useUserStore } from '@/stores/user';
import Api from '@/api/Api';


export default {
	name: 'NewAppointmentPage',
	components: {
		Form, Field, CellGroup,
		Popup, Picker,
		Button, MultiSelect,
		Switch, Icon,
		Calendar, TimePicker,
		SelectableRange,
	},
	setup() {
		const specialities = ref([]);
		const patients = ref([]);

		const doctors = ref('');
		const doctorsCodes = ref([]);

		const patient = ref('');
		const patientCode = ref(0);
		const patientIsAdult = ref(true);
		const showPatientPicker = ref(false);
		const onPatientConfirm = ({ selectedOptions }) => {
			patient.value = selectedOptions[0]?.name;
			patientCode.value = selectedOptions[0]?.id;
			patientIsAdult.value = selectedOptions[0]?.is_adult;
			showPatientPicker.value = false;
			speciality.value = '';
			specialityCode.value = 0;
			doctors.value = '';
			doctorsCodes.value = [];
		};

		const speciality = ref('');
		const specialityCode = ref(0);
		const showSpecialityPicker = ref(false);
		const onSpecialityFieldClick = () => {
			if (patient.value) {
				showSpecialityPicker.value = true;
			}
		}
		const onSpecialityConfirm = ({ selectedOptions }) => {
			let specialityCodeValue = selectedOptions[0]?.uid;
			speciality.value = selectedOptions[0]?.title;
			specialityCode.value = specialityCodeValue;
			showSpecialityPicker.value = false;
			doctors.value = '';
			doctorsCodes.value = [];
		};

		const showDoctorsPicker = ref(false);
		const onDoctorsFieldClick = () => {
			if (speciality.value) {
				showDoctorsPicker.value = true;
			}
		}
		const onDoctorsConfirm = (selectedOptions) => {
			if (selectedOptions.length === 0) {
				doctors.value = '';
				doctorsCodes.value = [];
			} else if (selectedOptions.length === 1) {
				doctors.value = selectedOptions[0].name;
				doctorsCodes.value = [selectedOptions[0].uid];
			} else {
				doctors.value = selectedOptions.length + ' ' + pluralize(selectedOptions.length, 'врач', 'врача', 'врачей');
				let codes = [];
				for (let doctor of selectedOptions) {
					codes.push(doctor.uid);
				}
				doctorsCodes.value = codes;
			}
			showDoctorsPicker.value = false;
		};

		const lastDay = getDayStart(new Date());
		lastDay.setDate(lastDay.getDate() + 13);
		const calendarMaxDate = ref(lastDay);

		const calendarDefaultDatesStart = getDayStart(new Date());
		const calendarDefaultDatesEnd = getDayStart(new Date());
		calendarDefaultDatesEnd.setDate(calendarDefaultDatesEnd.getDate() + 1);
		const calendarDefaultDates = ref([calendarDefaultDatesStart, calendarDefaultDatesEnd]);

		const selectedDays = ref([calendarDefaultDatesStart, calendarDefaultDatesEnd]);
		const onDaysSelect = (selectedOptions) => {
			selectedDays.value = selectedOptions;
		}
		const onDaysUnselect = (unselectedDate) => {
			selectedDays.value = selectedDays.value.filter(item => item.getTime() !== unselectedDate.getTime());
		}

		const quickMode = ref(true);

		const timePeriods = ref(arrayRange(TIME_PERIODS_START, TIME_PERIODS_END));

		const verificationCode = ref('');
		const telegramUsername = ref('');
		const verificationTimer = ref(false);
		const isDataLoaded = ref(false);
		const userStore = useUserStore();
		const isCreatingAppointment = ref(false);
		return {
			patient,
			patientCode,
			patientIsAdult,
			showPatientPicker,
			onPatientConfirm,

			speciality,
			specialityCode,
			showSpecialityPicker,
			onSpecialityFieldClick,
			onSpecialityConfirm,
			specialities,

			doctors,
			doctorsCodes,
			showDoctorsPicker,
			onDoctorsFieldClick,
			onDoctorsConfirm,

			selectedDays,
			calendarMaxDate,
			calendarDefaultDates,
			onDaysSelect,
			onDaysUnselect,
			GREEN_COLOR,

			quickMode,

			TIME_PERIODS_START,
			TIME_PERIODS_END,
			timePeriods,

			verificationCode,
			telegramUsername,
			verificationTimer,

			isDataLoaded,
			userStore,
			isCreatingAppointment,
		};
	},
	computed: {
		specialitiesDisplay() {
			if (this.patientCode == 0) {
				return [];
			}
			return this.specialities.filter(speciality => {
				if (this.patientIsAdult) {
					return speciality.forAdults;
				} else {
					return speciality.forChildren;
				}
			})
		},
		doctorsList() {
			if (this.specialityCode == 0) {
				return [];
			}
			return this.specialitiesDisplay.filter(spec => spec.uid == this.specialityCode)[0].doctors.filter(doctor => {
				if (this.patientIsAdult) {
					return doctor.forAdults;
				} else {
					return doctor.forChildren;
				}
			});
		},
		selectedTimeRangeText() {
			return getSelectedTimeRangeText(this.timePeriods, TIME_PERIODS_START, TIME_PERIODS_END);
		},
		selectedDoctorScheduleIntersections() {
			let intersections = getAllSchedulesIntersectionsFormatSafe(this.doctorsList, this.doctorsCodes, this.selectedDays, this.timePeriods);
			return intersections.map(item => ({
				uid: item[0],
				doctor: item[1],
				exists: item[2].length > 0,
			}));
		},
		autoGroupDisabled() {
			return (
				this.specialityCode !== 0 && // специальность выбрана
				this.doctorsCodes.length === 0 && // доктора вручную не выбраны
				this.doctorsList.length !== 1 && // список докторов состоит не из одного
				this.doctorsList.filter(doctor => doctor.autoGroup).length === 0 // в списке ни одного доктора с autoGroup
			)
		},
		verificationLink() {
			return 'https://t.me/doctorsp_test_bot?start=' + this.verificationCode;
		},
	},
	mounted() {
		Api.getDoctors(this.updateDoctors);
	},
	methods: {
		onCreatedAppointment(data) {
			showNotify({ type: 'success', message: 'Заявка добавлена' })
			this.$router.push({ name: 'appointments' });
		},
		onSubmit() {
			if (this.patientCode === 0) {
				showToast({
					message: 'Укажите пациента',
					position: 'bottom',
				});
				return ;
			}
			if (this.specialityCode === 0) {
				showToast({
					message: 'Укажите специальность врача',
					position: 'bottom',
				});
				return ;
			}
			if (this.selectedDays.length === 0) {
				showToast({
					message: 'Укажите даты',
					position: 'bottom',
				});
				return ;
			}
			if (this.telegramUsername.length === 0) {
				showToast({
					message: 'Оставьте телеграм для уведомлений',
					position: 'bottom',
				});
				return ;
			}
			if (this.autoGroupDisabled) {
				showToast({
					message: 'В рамках выбранной специальности необходимо выбрать конкретных врачей вручную',
					position: 'bottom',
				});
				return ;
			}
			const data = {
				patient: this.patientCode,
				department: this.specialityCode,
				doctors: this.doctorsCodes,
				date_condition: datesToStrings(this.selectedDays),
				quick_mode: this.quickMode,
				time_condition: this.timePeriods,
			}
			this.isCreatingAppointment = true;
			Api.createAppointment(data, this.onCreatedAppointment)
		},
		updateDoctors(data) {
			this.patients = data.patients;
			this.specialities = data.specialities;
			this.verificationCode = data.profile.verificationCode;
			this.telegramUsername = data.profile.telegramUsername;
			if (data.profile.verificationCode) {
				const checkVerificationHandler = data => {
					this.verificationCode = data.profile.verificationCode;
					this.telegramUsername = data.profile.telegramUsername;
					if (this.telegramUsername) {
						clearInterval(this.verificationTimer);
					}
				};
				this.verificationTimer = setInterval(() => Api.checkVerification(checkVerificationHandler), 1000);
			}
			this.isDataLoaded = true;
		},
	}
}
</script>

<style scoped>
.calendar {
	height: 50vh;
}

.group-padding {
	margin-top: var(--van-padding-md);
}

.intersection-doctor-name {
	padding-right: var(--van-padding-md);
	vertical-align: top;
}
.intersection-status {
	vertical-align: top;
}

.telegram-confirmation-link {
	border: 1px solid #109000;
	padding: 0 5px;
	border-radius: 3px;
	background-color: #13A10E;
	color: white;
	white-space: nowrap;
}

.telegram-confirmation-link:hover {
	background-color: #34C22F;
}
</style>

<style>
.wide-field .van-field__label {
	width: 50%;
}
</style>
